import { createContext, CSSProperties, useContext } from 'react';

import { Attributes, mergeAttributes, Node, NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { BaseIcon } from '@components/icons/base';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconCompany } from '@components/icons/company';
import { IconDropdown } from '@components/icons/dropdown';
import { IconFormatter } from '@components/icons/formatter';
import { IconInfo } from '@components/icons/info';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconSidemenuRightOpen } from '@components/icons/sidemenu-right-open';
import { IconSubIndicator } from '@components/icons/sub-indicator';
import { IconTextShort } from '@components/icons/text-short';
import { IconTraveller } from '@components/icons/traveller';
import { PopoverButton } from '@components/popover-button';
import { Stack } from '@components/stack';
import { Text } from '@components/text/text';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { FORM_LINK_FIELD } from './addons/field-mapping';
import type { FieldMappingGroup, FormLinkDraft, FormLinkType } from './addons/field-mapping';
import { fieldTypeIconMap } from './addons/field-mapping-type-maps';
import { findVariableField, findVariableGroup } from './addons/field-mapping-variable-label';
import { FormatDropdown } from './addons/format-dropdown';
import {
  buildFormatAttributeUpdates,
  getFormatPresetLabel,
  isFormattableType,
  isMatchingPreset,
} from './addons/format-presets';
import { useFormatPresets } from './addons/format-presets-context';
import { LinkTypeDropdown } from './addons/link-type-popover';
import type { PillVariant } from './addons/link-type-popover';
import { ActionIcon, actionIcons } from './common';
// type-only: a value import here creates a runtime cycle (helpers → sidebar → field-mapping → helpers)
import type { FieldMappingSidebarProps } from './field-mapping-sidebar';

type AttributeNames = string[];

export function createPlaceholderHtml({
  tag,
  attributes,
}: {
  tag: string;
  attributes: AttributeNames;
}) {
  return (attributeObject: { [key in (typeof attributes)[number]]: string }) => {
    return `<${tag} ${attributes
      .map((name) => {
        const value = String(attributeObject[name] ?? '')
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;');
        return `${name}="${value}"`;
      })
      .join(' ')}></${tag}>`;
  };
}

type CreatePlaceholderProps = {
  tag: string;
  attributes: AttributeNames;
  renderText(props: any): any;
  nodeComponent?(props: NodeViewProps): JSX.Element;
};

export const createPlaceholderNode = ({
  tag,
  attributes,
  renderText,
  nodeComponent = PlaceholderContainer,
}: CreatePlaceholderProps) => {
  return Node.create({
    name: tag,
    tag,
    group: 'inline',
    inline: true,
    addAttributes: () => getAttributes(attributes),
    parseHTML: () => [{ tag }],
    renderHTML: (props) => [tag, mergeAttributes(props.HTMLAttributes)],
    renderText: (props) => renderText(props.node.attrs),
    addNodeView: () => ReactNodeViewRenderer(nodeComponent),
  });
};

export const getAttributes = (attributes: string[]): Attributes => {
  return attributes.reduce((accumulated, attributeName) => {
    accumulated[attributeName] = {
      default: null,
      keepOnSplit: true,
      parseHTML: (element) => element.getAttribute(attributeName),
      renderHTML: (elementAttributes) => {
        const attribute = elementAttributes[attributeName];
        return attribute ? { [attributeName]: attribute } : {};
      },
    };
    return accumulated;
  }, {} as Attributes);
};

type VariantAttributes = {
  backgroundColor: Color;
  hoverBackgroundColor: Color;
  borderColor: Color;
  contentColor: Color;
  borderRadius: CSSProperties['borderRadius'];
};

const VARIANT_MAP: Record<string, VariantAttributes> = {
  fieldMapped: {
    backgroundColor: 'blueTrans10',
    hoverBackgroundColor: 'blueTrans20',
    borderColor: 'elementBlue100',
    contentColor: 'elementBlue100',
    borderRadius: pxToRem(16),
  },
  fixed: {
    backgroundColor: 'greenTrans10',
    hoverBackgroundColor: 'greenTrans20',
    borderColor: 'elementGreen100',
    contentColor: 'elementGreen200',
    borderRadius: pxToRem(4),
  },
  // An invalid fixed value (e.g. a malformed email) — squared like `fixed`, just red.
  error: {
    backgroundColor: 'redTrans10',
    hoverBackgroundColor: 'redTrans20',
    borderColor: 'elementRed100',
    contentColor: 'elementRed100',
    borderRadius: pxToRem(4),
  },
  // A broken mapped variable (missing source) — keeps the rounded mapped-pill shape, just red.
  fieldMappedError: {
    backgroundColor: 'redTrans10',
    hoverBackgroundColor: 'redTrans20',
    borderColor: 'elementRed100',
    contentColor: 'elementRed100',
    borderRadius: pxToRem(16),
  },
};

type PlaceholderContainerProps = NodeViewProps & {
  icon?: typeof BaseIcon;
  variant?: keyof typeof VARIANT_MAP;
  tooltip?: string;
  hideRemove?: boolean;
};

export const PlaceholderContainer = ({
  variant = 'fixed',
  node,
  icon: Icon,
  deleteNode,
  updateAttributes,
  editor,
  getPos,
  tooltip,
  hideRemove,
}: PlaceholderContainerProps) => {
  const { contentColor } = VARIANT_MAP[variant];
  const { isEditMode, disableExpandable } = useFieldMappingContext();
  const attributes = node.attrs;
  const isFieldMapped = variant === 'fieldMapped';

  const content = (
    <>
      {Icon && <Icon size={16} color={contentColor} />}
      <Text
        style={{ minWidth: 0, maxWidth: pxToRem(250) }}
        ellipsis
        font="semibold12"
        color={contentColor}
        tooltip={tooltip}
        ellipsisWithTooltip
      >
        {attributes.label ?? attributes.value}
      </Text>
      {!hideRemove && isEditMode && (
        <RemoveButton
          color={contentColor}
          onClick={(e) => {
            e.stopPropagation();
            const pos = getPos();
            deleteNode();
            if (editor.isEditable) {
              editor.commands.focus(pos);
            } else {
              (editor.view.dom.closest('[tabindex]') as HTMLElement)?.focus();
            }
          }}
        />
      )}
    </>
  );

  if (!isFieldMapped || !isEditMode || disableExpandable) {
    return (
      <NodeWrapper className="node-wrapper">
        <ContentContainer $variant={variant} $isClickable={false}>
          {content}
        </ContentContainer>
      </NodeWrapper>
    );
  }

  return (
    <ExpandablePlaceholder node={node} variant={variant} updateAttributes={updateAttributes}>
      {content}
    </ExpandablePlaceholder>
  );
};

export type FormatPresetData = {
  id: string;
  name: string;
  format: string;
  formatLanguage?: string;
  isSystem?: boolean;
  isDefault?: boolean;
};

export type FieldMappingContextValue = {
  showFieldMappingSidebar: (props?: Pick<FieldMappingSidebarProps, 'variable'>) => void;
  variableGroups: FieldMappingGroup[];
  isEditMode: boolean;
  hasFormatting: boolean;
  disableExpandable?: boolean;
  formLinkType?: {
    isEnabled: boolean;
    onConfigOpen?(): void;
    onDraftChange?(draft: FormLinkDraft | null): void;
  };
};

export const FieldMappingContext = createContext<FieldMappingContextValue>({
  showFieldMappingSidebar: () => {},
  variableGroups: [],
  isEditMode: true,
  hasFormatting: true,
});

export const useFieldMappingContext = () => {
  return useContext(FieldMappingContext);
};

type ExpandablePlaceholderProps = Pick<PlaceholderContainerProps, 'node' | 'variant'> & {
  children: JSX.Element;
  updateAttributes: NodeViewProps['updateAttributes'];
};

const ExpandablePlaceholder = ({
  node,
  variant,
  children,
  updateAttributes,
}: ExpandablePlaceholderProps) => {
  const { showFieldMappingSidebar, variableGroups, hasFormatting, formLinkType, isEditMode } =
    useFieldMappingContext();
  const { formatPresets, onSaveFormatPreset, onEditFormatPreset, onRemoveFormatPreset } =
    useFormatPresets();
  const attributes = node.attrs;

  const variableGroup = findVariableGroup(attributes, variableGroups);

  const getGroupIcon = (): ActionIcon => {
    if (!variableGroup) {
      return { Icon: IconTextShort };
    }

    if (variableGroup.action) {
      return (
        actionIcons[variableGroup.action] ?? {
          Icon: IconTextShort,
        }
      );
    }

    if (variableGroup.id === 'traveller') {
      return { Icon: IconTraveller };
    }

    if (variableGroup.id === 'company') {
      return { Icon: IconCompany };
    }
    if (
      variableGroup.id === 'journey' ||
      variableGroup.id === 'journeyFields' ||
      variableGroup.id === 'journeyData'
    ) {
      return { Icon: IconJourneyItem };
    }

    return { Icon: IconTextShort };
  };

  const { Icon: GroupIcon, color: groupIconColor } = getGroupIcon();
  const field = variableGroup ? findVariableField(attributes, variableGroup.fields) : undefined;
  const FieldIcon =
    (field && field.type && field.type in fieldTypeIconMap
      ? fieldTypeIconMap[field.type as keyof typeof fieldTypeIconMap]
      : undefined) ?? IconTextShort;

  const hasFormatter = hasFormatting && isFormattableType(attributes.type);
  const matchingPreset = formatPresets.find((preset) =>
    isMatchingPreset(preset, attributes.format, attributes.formatLanguage)
  );
  const canEditMatchingPreset = Boolean(
    matchingPreset && !matchingPreset.isSystem && onEditFormatPreset
  );

  const editPresetForPlaceholder = onEditFormatPreset
    ? (preset: FormatPresetData) => {
        const isCurrentlySelected = isMatchingPreset(
          preset,
          attributes.format,
          attributes.formatLanguage
        );
        onEditFormatPreset(preset, {
          onApply: isCurrentlySelected
            ? (format, formatLanguage) => {
                updateAttributes(buildFormatAttributeUpdates(format, formatLanguage));
              }
            : undefined,
        });
      }
    : undefined;

  const popover = (
    <PopoverButton
      trigger={ContentContainer}
      triggerProps={{
        $variant: variant,
        children,
      }}
      placement="bottom-start"
      contentStyle={{
        minWidth: 'fit-content',
      }}
      popoverStyle={{ display: 'inline', minWidth: 0 }}
    >
      {({ close }) => (
        <ExpandedSection>
          <Stack space={2} orientation="horizontal" justify="space-between" align="center">
            <Stack space={1} style={{ minWidth: 0 }}>
              <Stack orientation="horizontal" space={1} align="center" style={{ minWidth: 0 }}>
                <GroupIcon size={16} color={groupIconColor ?? 'elementPurple60'} />
                <Text
                  text={variableGroup?.label}
                  font="regular12"
                  ellipsisWithTooltip
                  style={{ minWidth: 0 }}
                />
                {variableGroup?.description && (
                  <IconInfo size={16} color="elementPurple50" tooltip={variableGroup.description} />
                )}
              </Stack>
              <Stack orientation="horizontal" space={1} align="center" style={{ minWidth: 0 }}>
                <IconSubIndicator color="elementPurple60" size={16} />
                <FieldIcon size={16} color="elementPurple60" />
                <Text
                  text={field?.label}
                  font="regular12"
                  ellipsisWithTooltip
                  style={{ minWidth: 0 }}
                />
              </Stack>
              {hasFormatter && attributes.format && attributes.format !== 'undefined' && (
                <FormatRow
                  $clickable={canEditMatchingPreset}
                  onClick={
                    canEditMatchingPreset && matchingPreset && editPresetForPlaceholder
                      ? (e) => {
                          e.stopPropagation();
                          close();
                          editPresetForPlaceholder(matchingPreset);
                        }
                      : undefined
                  }
                >
                  <IconSubIndicator color="elementPurple60" size={16} />
                  <IconFormatter size={16} color="elementPurple60" />
                  <Text
                    text={attributes.format}
                    font="regular12"
                    color="textSecondary"
                    ellipsisWithTooltip
                    style={{ minWidth: 0 }}
                  />
                </FormatRow>
              )}
            </Stack>
            {/* Field types that open the sidebar on click (file, date, email,
                phone, number) wrap the editor in a click handler that opens it
                with no variable — let this win so the variable stays focused. */}
            <IconButton
              size="md"
              variant="ghost-purple"
              stopPropagation
              icon={<IconSidemenuRightOpen size={24} color="buttonIconPositive" />}
              onClick={() => {
                showFieldMappingSidebar({
                  variable: field,
                });
                close();
              }}
            />
          </Stack>
        </ExpandedSection>
      )}
    </PopoverButton>
  );

  // Keying on the field name is safe here because this is the editor pill, not the resolved
  // variable (the backend deliberately keys on link-type presence because self-heal renames the
  // variable to `<shortId>_unique_link`).
  if (attributes.field === FORM_LINK_FIELD && formLinkType?.isEnabled && isEditMode) {
    return (
      <NodeWrapper className="node-wrapper">
        <PillWithFormat>
          {popover}
          <LinkTypeDropdown
            linkType={(attributes.linkType as FormLinkType) || 'url'}
            linkLabel={attributes.linkLabel ?? ''}
            variableName={attributes.variable}
            // Safe: ExpandablePlaceholder (this component) only renders when
            // PlaceholderContainer's isFieldMapped check already narrowed
            // variant to 'fieldMapped' — VARIANT_MAP's keys aren't a literal
            // union, so the cast documents that guarantee.
            variant={variant as PillVariant}
            trigger={FormatTrigger}
            onOpen={formLinkType.onConfigOpen}
            onDraftChange={formLinkType.onDraftChange}
            onCommit={(draft) =>
              updateAttributes({ linkType: draft.linkType, linkLabel: draft.linkLabel })
            }
          />
        </PillWithFormat>
      </NodeWrapper>
    );
  }

  if (hasFormatter) {
    const { contentColor: variantColor } = VARIANT_MAP[variant ?? 'fieldMapped'];
    const formatLabel = getFormatPresetLabel(
      attributes.format,
      attributes.formatLanguage,
      formatPresets
    );

    return (
      <NodeWrapper className="node-wrapper">
        <PillWithFormat>
          {popover}
          <FormatDropdown
            format={attributes.format}
            formatLanguage={attributes.formatLanguage}
            presets={formatPresets}
            onSavePreset={onSaveFormatPreset}
            onEditPreset={editPresetForPlaceholder}
            onRemovePreset={onRemoveFormatPreset}
            onFormatChange={(format, formatLanguage) => {
              updateAttributes(buildFormatAttributeUpdates(format, formatLanguage));
            }}
            trigger={FormatTrigger}
            triggerProps={{
              $variant: variant,
              children: (
                <>
                  <IconFormatter size={14} color={variantColor} />
                  <Text
                    text={formatLabel}
                    font="semibold12"
                    color={variantColor}
                    ellipsis
                    style={{ minWidth: 0, maxWidth: pxToRem(120) }}
                  />
                  <IconDropdown size={16} color={variantColor} />
                </>
              ),
            }}
          />
        </PillWithFormat>
      </NodeWrapper>
    );
  }

  return <NodeWrapper className="node-wrapper">{popover}</NodeWrapper>;
};

const NodeWrapper = styled(NodeViewWrapper)({
  // inline-flex, not inline-block: with an inline-level child (PillWithFormat) an inline-block
  // builds an internal line box from the inherited line-height and grows past the pill height
  display: 'inline-flex',
  verticalAlign: 'middle',
  maxWidth: '100%',
  // horizontal gap so adjacent pills (and pills next to text) don't touch
  marginRight: pxToRem(8),
});

// Pill geometry — the single source of truth the editors build their line-height from. The pill is
// one text line tall; line-height carries an extra PILL_ROW_GAP so a single pill never outgrows the
// line (no shift) and stacked pills on wrapped rows don't touch.
export const PILL_HEIGHT_PX = 24;
export const PILL_ROW_GAP_PX = 2;
// Dead-centers the pill in its PILL_HEIGHT + PILL_ROW_GAP line: top-align to the line box and push
// down half the gap. Deterministic — vertical-align: middle depends on font metrics and sat ~2px
// high of center. Apply to `.node-wrapper` in any editor using the pill line-height.
export const pillRowAlignment = {
  verticalAlign: 'top',
  marginTop: pxToRem(PILL_ROW_GAP_PX / 2),
} as const;
// Multi-line field-mapping editors rest at 3 rows tall plus the one-gap top pad — the height a
// formatted 3-line value occupies. Shared so call sites can pin to the same resting height.
export const FIELD_MAPPING_REST_MIN_HEIGHT_PX =
  3 * (PILL_HEIGHT_PX + PILL_ROW_GAP_PX) + PILL_ROW_GAP_PX;

const getVariantStyles = (variant: keyof typeof VARIANT_MAP = 'fixed') => {
  const variantStyles = VARIANT_MAP[variant];
  return {
    backgroundColor: theme.color[variantStyles.backgroundColor],
    border: `1px solid ${theme.color[variantStyles.borderColor]}`,
    borderRadius: variantStyles.borderRadius,
    padding: `0 ${pxToRem(8)}`,
    height: pxToRem(PILL_HEIGHT_PX),
    transition: 'background-color 150ms ease-out',
    '&:hover': {
      backgroundColor: theme.color[variantStyles.hoverBackgroundColor],
    },
  } as const;
};

const FormatTrigger = styled.button<{
  $variant?: PlaceholderContainerProps['variant'];
}>(({ $variant = 'fieldMapped' }) => ({
  ...getVariantStyles($variant),
  display: 'inline-flex',
  alignItems: 'center',
  minWidth: 0,
  gap: pxToRem(4),
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  outline: 'none',
}));

const PillWithFormat = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  maxWidth: '100%',
  // PopoverButton wraps the left pill in a plain block <div>. That div's line
  // box is 2px taller than the pill inside it, so the row grew to 26px and the
  // type pill settled 2px below the variable pill. Zeroing the line-height
  // collapses the wrapper onto its content; the pill sets its own line-height.
  '& > div': {
    lineHeight: 0,
  },
  '.placeholder-container': {
    minWidth: 0,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRight: 'none',
  },
});

const ContentContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 1,
  className: 'placeholder-container',
})<{
  $variant: PlaceholderContainerProps['variant'];
  $isClickable?: boolean;
}>(({ $variant = 'fixed', $isClickable = true }) => ({
  ...getVariantStyles($variant),
  cursor: $variant === 'fieldMapped' && $isClickable ? 'pointer' : 'default',
}));

const RemoveButton = styled(IconCloseRegular).attrs({ size: 16 })({
  cursor: 'pointer',
});

const ExpandedSection = styled.div({
  width: pxToRem(340),
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(8),
  padding: `${pxToRem(8)} ${pxToRem(16)}`,
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
});

const FormatRow = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 1,
  align: 'center',
})<{ $clickable?: boolean }>(({ $clickable = true }) => ({
  cursor: $clickable ? 'pointer' : 'default',
  borderRadius: pxToRem(4),
  padding: `${pxToRem(2)} ${pxToRem(4)}`,
  marginLeft: pxToRem(-4),
  minWidth: 0,
  ...($clickable && {
    '&:hover': {
      backgroundColor: theme.color.blueTrans10,
    },
  }),
}));
